import type { LegacyCustomer, LegacyDealer } from "../api/types";

// The old shop's roles, in the names its dealers use: the tiers (legacy role
// 1..5) and the two customer roles.
const ROLE: Record<number, string> = {
  1: "T1 Agent",
  2: "T2 Leader",
  3: "T3 Excellence Leader",
  4: "T4 Elite Leader",
  5: "T5 Distributor",
  700: "顾客",
  701: "VIP 顾客",
};

// What the match rests on when it is not the chat's own number, so a
// colleague knows how sure it is.
const VIA: Record<string, string> = { attached: "同事加的号码", merged: "合并进来的另一个号码", typed: "客户在对话里打的号码" };

type Words = { cls: string; short: string; full: string; title: string; about: string };

// What the tag says about a dealer or a dealer's customer: the short tag, the
// full one (the tier's name, the upline), the hover, and who the old shop has
// them as.
function words(d?: LegacyDealer | null, c?: LegacyCustomer | null): Words | null {
  if (d) {
    const tier = d.tier >= 1 && d.tier <= 5 ? ROLE[d.tier] : "";
    const via = d.via ? VIA[d.via] ?? "" : "";
    return {
      cls: "zn-dealer",
      short: tier ? `代理 T${d.tier}` : "代理",
      full: tier ? `代理 · ${tier}` : "代理",
      title: ["旧系统的代理", d.name, tier, via].filter(Boolean).join(" · "),
      about: ["旧系统的代理", d.name, via].filter(Boolean).join(" · "),
    };
  }
  if (!c) return null;
  const up = c.upline;
  const word = up?.company ? "公司顾客" : "代理顾客";
  const upline = up?.name && !up.company ? `上家 ${up.name}${up.role && ROLE[up.role] ? `（${ROLE[up.role]}）` : ""}` : "";
  const what = c.account ? (c.vip ? "旧系统的 VIP 顾客" : "旧系统的顾客") : "旧系统寄过货到这个号码";
  const via = c.via ? VIA[c.via] ?? "" : "";
  return {
    cls: "zn-dealer cust",
    short: word,
    full: upline ? `${word} · ${upline}` : word,
    title: [what, c.name, upline, via].filter(Boolean).join(" · "),
    about: [what, c.name, via].filter(Boolean).join(" · "),
  };
}

/**
 * 代理 beside a name: this person is one of the old shop's dealers, and which
 * tier; 代理顾客: one of a dealer's customers, and whose (owner, 2026-10-19) —
 * 公司顾客 when they buy straight from the company. The server matched the
 * chat's own number, one a colleague attached, or — in a chat with no number
 * of its own — one the customer typed, against the old shop's accounts and
 * the numbers it shipped paid parcels to (owner, 2026-09-30: dealers message
 * the ads like anybody else and were answered as new customers). Nothing when
 * the old shop does not know them.
 *
 * A list says 代理 T3 / 代理顾客 and the hover the rest. `fit` (the chat
 * header) says the tier's name and the upline where there is room, and the
 * short tag on a phone, whose header has none (DealerLine says it there).
 *
 * The progress word 代理's pink, because it is the same fact about the person;
 * unfilled for a customer, who is about a dealer rather than one. A tag beside
 * the name rather than a dot, because it is who they are, not how far they
 * got. An <i>, like 同一个人: the chat header styles every span in it as a line
 * of its own.
 */
export default function DealerTag({ d, c, fit }: { d?: LegacyDealer | null; c?: LegacyCustomer | null; fit?: boolean }) {
  const w = words(d, c);
  if (!w) return null;
  if (fit && w.full !== w.short) {
    return (
      <>
        <i className={`${w.cls} zn-dealer-full`} title={w.title}>
          {w.full}
        </i>
        <i className={`${w.cls} zn-dealer-short`} title={w.title}>
          {w.short}
        </i>
      </>
    );
  }
  return (
    <i className={w.cls} title={w.title}>
      {w.short}
    </i>
  );
}

/**
 * The tag in full and who the old shop has them as, for 客户详情 on a phone:
 * there is no hover there, and the header has room for the short tag only.
 */
export function DealerLine({ d, c }: { d?: LegacyDealer | null; c?: LegacyCustomer | null }) {
  const w = words(d, c);
  if (!w) return null;
  return (
    <section className="wc-sec wc-legacy">
      <h4>旧系统</h4>
      <p>
        <i className={w.cls}>{w.full}</i>
      </p>
      <p className="wc-why">{w.about}</p>
    </section>
  );
}
